import { useAuth } from '../../../hooks/AuthProvider';
import { CTWA_MEDIA_UPLOAD, CTWA_MEDIA_CAROUSEL } from '../../../config/urls';
import { ASPECT_RATIOS, ASPECT_RATIO_TOLERANCE } from './constants';

// Authenticated fetch for the CTWA screens. Throws Error(message) on a non-2xx reply.
export const useCtwaApi = () => {
  const { getValidToken } = useAuth();

  const request = async (url, { method = 'GET', body, form } = {}) => {
    const token = await getValidToken();
    const headers = { Authorization: `Bearer ${token}`, 'X-Tenant-Id': localStorage.getItem('tenant') };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(url, {
      method,
      headers,
      body: form || (body !== undefined ? JSON.stringify(body) : undefined),
    });
    let data = {};
    try { data = await res.json(); } catch (_) { /* empty or non-JSON body */ }
    if (!res.ok) throw new Error(data.error || data.detail || `Request failed (${res.status})`);
    return data;
  };

  return {
    get: (url) => request(url),
    post: (url, body) => request(url, { method: 'POST', body }),
    put: (url, body) => request(url, { method: 'PUT', body }),
    del: (url) => request(url, { method: 'DELETE' }),
    upload: (url, form) => request(url, { method: 'POST', form }),
  };
};

// ── Media ────────────────────────────────────────────────────────────────────

export const ratioOf = (width, height) => {
  if (!width || !height) return '';
  const actual = width / height;
  const match = ASPECT_RATIOS.find(({ w, h }) => Math.abs(actual - w / h) <= (w / h) * ASPECT_RATIO_TOLERANCE);
  return match ? match.value : '';
};

// Reads an image's or video's pixel size in the browser: { width, height, ratio }.
export const readMediaSize = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const done = (width, height) => resolve({ width, height, ratio: ratioOf(width, height), url });
  if (file.type.startsWith('video/')) {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => done(video.videoWidth, video.videoHeight);
    video.onerror = () => reject(new Error('This video could not be read.'));
    video.src = url;
  } else {
    const img = new Image();
    img.onload = () => done(img.naturalWidth, img.naturalHeight);
    img.onerror = () => reject(new Error('This image could not be read.'));
    img.src = url;
  }
});

export const ratioError = (width, height) =>
  `This file is ${width}×${height}. Use a 1:1, 4:5 or 9:16 image or video.`;

// Uploads one ad's media (S3 + Meta) and returns the media asset id.
export const uploadAdMedia = async (api, adAccountId, ad) => {
  const form = new FormData();
  form.append('ad_account_id', adAccountId);
  if (ad.format === 'CAROUSEL') {
    form.append('cards', JSON.stringify(ad.cards.map((c) => ({ headline: c.headline, description: c.description }))));
    ad.cards.forEach((c) => form.append('files', c.file));
    return (await api.upload(CTWA_MEDIA_CAROUSEL, form)).id;
  }
  form.append('media_type', ad.format);
  form.append('file', ad.file);
  if (ad.format === 'VIDEO') {
    form.append('width', ad.width);
    form.append('height', ad.height);
  }
  return (await api.upload(CTWA_MEDIA_UPLOAD, form)).id;
};

// An ad still needs uploading when it has local files but no asset yet.
export const needsUpload = (ad) =>
  !ad.mediaAssetId && (ad.format === 'CAROUSEL' ? ad.cards.every((c) => c.file) : !!ad.file);
